import React, { useRef, useState } from 'react';
import {
  ButtonWithIcon,
  Card,
  DropdownButton,
  IconContent,
  IconSection,
  ImageButtonContainer,
  ImageInputContainer,
  Question,
  Tag,
  TagSection,
  TextAreaContainer,
} from './styled.questionCard';
import { TextArea } from './../../../common/sharedComponents/TextArea';
import { Like } from './../../../common/icons/Like';
import { Dislike } from './../../../common/icons/Dislike';
import { Note } from '../../../common/icons/Note';
import { AddPhoto } from './../../../common/icons/AddPhoto';
import { IMyAuditQuestionCard } from './../../../@types/questionCard';
import { Image } from './../../../common/icons/Image';
import { DropdownIcon } from './../../../common/icons/DropdownIcon';
import { useTranslation } from 'react-i18next';
import { Cropper } from './../../../common/sharedComponents/Cropper';
import { SCORING_TYPES } from '../../../../utils/constants';
import { AttachmentModal } from './AttachmentModal';
import { DeleteAttachmentModal } from './DeleteAttachmentModal';
import { StandardsBottomSheet } from './StandardsBottomSheet';
import { ScoreTypes } from '../../../@types/genericTypes';
import { Loading } from './../../../common/icons/Loading';

export function QuestionCard ({
  question,
  onClickScore,
  handleQuestionComment,
  handleMediaUpload,
  showDropdownIcon,
  isView,
  sectionId,
  onDelete,
  handleReplaceImage,
  questionImgLoading,
  scoringType,
}: IMyAuditQuestionCard) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isTextAreOpen, setTextAreaOpen] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isBottomSheetOpen, setBottomSheetOpen] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [fileId, setFileId] = useState<number>(0);

  const isQuestionScore: boolean =
    question.score === 1 || question.score === 0 || question.score === null;

  const handleImageCompletion = (croppedImage: string | File) => {
    if (question.attachment && question.attachment.url) {
      if (handleReplaceImage) {
        handleReplaceImage(
          croppedImage,
          0,
          fileId,
          true,
          question.id,
          sectionId
        );
      }
    } else {
      if (handleMediaUpload) {
        handleMediaUpload(croppedImage, true, sectionId, question.id);
      }
      setImageFile(null);
    }
  };

  const handleTextAreaChange = (newValue: string) => {
    if (handleQuestionComment) {
      handleQuestionComment(newValue, question.id);
    }
  };

  function onClickArrow () {
    setTextAreaOpen(!isTextAreOpen);
  }

  function showIcon (label) {
    if (!question || !question.scoringTypeValues) {
      return true;
    }
    return (
      question.scoringTypeValues.length > 0 &&
      question.scoringTypeValues.some((item) => item.label === label)
    );
  }

  const isShowImageIcon =
    (question && question.attachment && question.attachment.url) || !isView;

  function handlePhoto () {
    if (isView) {
      handleOpenModalImg();
      return;
    }

    if (question.attachment && question.attachment.url) {
      setFileId(question.attachment.id);
      handleOpenBottomsheet();
    } else {
      if (inputRef.current) {
        inputRef.current.click();
      }
    }
  }

  function handleOpenBottomsheet () {
    setBottomSheetOpen(!isBottomSheetOpen);
  }

  function handleOpenModalImg () {
    setModalOpen(!isModalOpen);
  }

  function handleShowDeleteModal () {
    setShowDeleteModal(!showDeleteModal);
  }

  function handleViewPhoto () {
    setBottomSheetOpen(false);
    handleOpenModalImg();
  }

  function handleReplacePhoto () {
    setBottomSheetOpen(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  }

  function handleDeleteModal () {
    handleOpenBottomsheet();
    handleShowDeleteModal();
  }

  const showTextAreaWithScore = question.score !== '';

  return (
    <>
      <Card>
        <TagSection>
          <Tag>{question.performanceClassification}</Tag>
          {showDropdownIcon && isQuestionScore && (
            <DropdownButton onClick={onClickArrow}>
              <DropdownIcon
                data-testid="expand-card-icon"
                rotate={isTextAreOpen ? '180' : '0'}
                color="battleShipGray"
              />
            </DropdownButton>
          )}
        </TagSection>
        <Question>
          {Number(question.order) + 1}. {question.text}
        </Question>
        <IconSection>
          <IconContent>
            {showIcon(t('COMMON.MY_AUDIT.CONDUCT.SCORE.MET')) && (
              <ButtonWithIcon
                data-testid="score-met"
                onClick={() => {
                  if (onClickScore) {
                    onClickScore(
                      question.score === SCORING_TYPES.MET
                        ? ''
                        : (SCORING_TYPES.MET as ScoreTypes),
                      question.id,
                      scoringType && scoringType.value
                    );
                  }
                  if (!isView) {
                    setTextAreaOpen(true);
                  }
                }}
                backgroundColor={
                  question.score === SCORING_TYPES.MET ? 'verdigris' : 'white'
                }
                borderColor={
                  question.score === SCORING_TYPES.MET
                    ? 'verdigris'
                    : 'platinum'
                }
              >
                <Like
                  strokeColor={
                    question.score === SCORING_TYPES.MET ? 'white' : undefined
                  }
                  fillColor={
                    question.score === SCORING_TYPES.MET
                      ? 'verdigris'
                      : undefined
                  }
                />
              </ButtonWithIcon>
            )}
            {showIcon(t('COMMON.MY_AUDIT.CONDUCT.SCORE.NOT_MET')) && (
              <ButtonWithIcon
                data-testid="score-not-met"
                onClick={() => {
                  if (onClickScore) {
                    onClickScore(
                      question.score === (SCORING_TYPES.NOT_MET as ScoreTypes)
                        ? ''
                        : (SCORING_TYPES.NOT_MET as ScoreTypes),
                      question.id,
                      scoringType && scoringType.value
                    );
                  }
                  if (!isView) {
                    setTextAreaOpen(true);
                  }
                }}
                backgroundColor={
                  question.score === SCORING_TYPES.NOT_MET
                    ? 'redCrayola'
                    : 'white'
                }
                borderColor={
                  question.score === SCORING_TYPES.NOT_MET
                    ? 'redCrayola'
                    : 'platinum'
                }
              >
                <Dislike
                  strokeColor={
                    question.score === SCORING_TYPES.NOT_MET
                      ? 'white'
                      : undefined
                  }
                  fillColor={
                    question.score === SCORING_TYPES.NOT_MET
                      ? 'redCrayola'
                      : undefined
                  }
                />
              </ButtonWithIcon>
            )}
            {showIcon(t('COMMON.MY_AUDIT.CONDUCT.SCORE.NA')) && (
              <ButtonWithIcon
                data-testid="score-na"
                onClick={() => {
                  if (onClickScore) {
                    onClickScore(
                      question.score === (SCORING_TYPES.NA as ScoreTypes)
                        ? ''
                        : (SCORING_TYPES.NA as ScoreTypes),
                      question.id,
                      scoringType && scoringType.value
                    );
                  }
                  if (!isView) {
                    setTextAreaOpen(true);
                  }
                }}
                backgroundColor={
                  question.score === SCORING_TYPES.NA ? 'silver' : 'white'
                }
                borderColor={
                  question.score === SCORING_TYPES.NA ? 'silver' : 'platinum'
                }
              >
                <Note
                  strokeColor={
                    question.score === SCORING_TYPES.NA ? 'white' : undefined
                  }
                  fillColor={
                    question.score === SCORING_TYPES.NA ? 'silver' : undefined
                  }
                />
              </ButtonWithIcon>
            )}
          </IconContent>
          {isShowImageIcon && (
            <ImageButtonContainer>
              <ButtonWithIcon
                data-testid="add-photo"
                onClick={() => handlePhoto()}
                borderColor={
                  question.attachment && question.attachment.url
                    ? 'verdigris'
                    : 'platinum'
                }
                backgroundColor={
                  question.attachment && question.attachment.url
                    ? 'verdigris'
                    : 'white'
                }
              >
                {questionImgLoading &&
                questionImgLoading.isLoading &&
                questionImgLoading.id === question.id ? (
                  <Loading
                    color={
                      question.attachment && question.attachment.url
                        ? 'white'
                        : 'oxfordBlue'
                    }
                  />
                ) : question.attachment && question.attachment.url ? (
                  <Image strokeColor="platinum" />
                ) : (
                  <AddPhoto />
                )}
              </ButtonWithIcon>
              <ImageInputContainer
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e && e.target && e.target.files && e.target.files[0]) {
                    const newImage = e.target.files[0];
                    setImageFile(newImage);
                    if (inputRef.current) {
                      inputRef.current.value = '';
                    }
                  }
                }}
              />
              {imageFile && (
                <Cropper
                  imageFile={imageFile}
                  onImageCompletion={handleImageCompletion}
                  previousImages={[]}
                />
              )}
            </ImageButtonContainer>
          )}
        </IconSection>
        {showTextAreaWithScore && isTextAreOpen && (
          <TextAreaContainer>
            <TextArea
              data-testid="standard-comment"
              textValue={question.comments}
              onValueChange={handleTextAreaChange}
              placeholder={t(
                'COMMON.MY_AUDIT.COMPONENTS.QUESTION.PLACEHOLDERS.ADD_YOUR_COMMENT'
              )}
              disabled={isView}
            />
          </TextAreaContainer>
        )}
      </Card>
      {isModalOpen && (
        <AttachmentModal
          isModalOpen={isModalOpen}
          handleOpenModalImg={handleOpenModalImg}
          question={question}
        />
      )}
      {showDeleteModal && (
        <DeleteAttachmentModal
          showDeleteModal={showDeleteModal}
          handleShowDeleteModal={handleShowDeleteModal}
          handleAttachmentDelete={onDelete}
          index={0}
          fileId={fileId}
          question={true}
          standardId={question.id}
        />
      )}
      {isBottomSheetOpen && (
        <StandardsBottomSheet
          isBottomSheetOpen={isBottomSheetOpen}
          handleOpenBottomsheet={handleOpenBottomsheet}
          handleViewPhoto={handleViewPhoto}
          handleReplacePhoto={handleReplacePhoto}
          handleDeleteModal={handleDeleteModal}
        />
      )}
    </>
  );
}
